import { TextField, Stack, Typography } from "@mui/material";
import { ComponentType } from "react";
import {
  BillingRuleConditionTarget,
  BillingRuleConditionOperator,
  BusinessEntityType,
  GetCustomerDetailsQuery,
} from "../../../graphql/generated";
import BusinessEntityInput from "../BillingRuleForm/value-selectors/BusinessEntityInput";
import DateInput from "../BillingRuleForm/value-selectors/DateInput";
import DurationInput from "../BillingRuleForm/value-selectors/DurationInput";
import GoodsInput from "../BillingRuleForm/value-selectors/GoodsInput";
import MileageInput from "../BillingRuleForm/value-selectors/MileageInput";
import TimeInput from "../BillingRuleForm/value-selectors/TimeInput";
import TractorInput from "../BillingRuleForm/value-selectors/TractorInput";
import TrailerInput from "../BillingRuleForm/value-selectors/TrailerInput";
import TrailerTypeSelect from "../BillingRuleForm/value-selectors/TrailerTypeSelect";
import NumberInput from "../BillingRuleForm/value-selectors/NumberInput";
import VolumeInput from "../BillingRuleForm/value-selectors/VolumeInput";
import ConditionStateSelect from "../BillingRuleForm/value-selectors/ConditionStateSelect";
import CityInput from "../BillingRuleForm/value-selectors/CityInput";
import { useTranslation } from "react-i18next";
import { testIds } from "./ConditionValueSelector.testIds";

export type ConditionValueSelectorComponentProps = {
  value: number | string | string[] | null;
  onChange: (value: number | string | string[] | null) => void;
  customer?: GetCustomerDetailsQuery["customerById"];
  label?: string | null;
  placeholder?: string;
};

export type ConditionValueSelectorComponent =
  ComponentType<ConditionValueSelectorComponentProps>;

const ConditionValueSelectorComponentByTarget: {
  [key in BillingRuleConditionTarget]: ConditionValueSelectorComponent | null;
} = {
  [BillingRuleConditionTarget.TrailerType]: (props) => (
    <TrailerTypeSelect {...props} sx={{ minWidth: 200, display: "flex" }} />
  ),
  [BillingRuleConditionTarget.Date]: DateInput,
  [BillingRuleConditionTarget.Otherwise]: null,
  [BillingRuleConditionTarget.Mileage]: MileageInput,
  [BillingRuleConditionTarget.Receiver]: (props) => (
    <BusinessEntityInput
      {...props}
      businessEntityType={BusinessEntityType.Receiver}
      sx={{ minWidth: 200, display: "flex" }}
    />
  ),
  [BillingRuleConditionTarget.Shipper]: (props) => (
    <BusinessEntityInput
      {...props}
      businessEntityType={BusinessEntityType.Shipper}
      sx={{ minWidth: 200, display: "flex" }}
    />
  ),
  [BillingRuleConditionTarget.Time]: TimeInput,
  [BillingRuleConditionTarget.Trailer]: (props) => (
    <TrailerInput {...props} sx={{ minWidth: 200, display: "flex" }} />
  ),
  [BillingRuleConditionTarget.Tractor]: (props) => (
    <TractorInput {...props} sx={{ minWidth: 200, display: "flex" }} />
  ),
  [BillingRuleConditionTarget.DetentionTime]: DurationInput,
  [BillingRuleConditionTarget.Goods]: (props) => (
    <GoodsInput {...props} sx={{ minWidth: 200, display: "flex" }} />
  ),
  [BillingRuleConditionTarget.Volume]: VolumeInput,
  [BillingRuleConditionTarget.FuelCost]: NumberInput,
  [BillingRuleConditionTarget.CustomerState]: ConditionStateSelect,
  [BillingRuleConditionTarget.ShipperState]: ConditionStateSelect,
  [BillingRuleConditionTarget.ReceiverState]: ConditionStateSelect,
  [BillingRuleConditionTarget.ShipperCity]: (props) => (
    <CityInput {...props} sx={{ minWidth: 200, display: "flex" }} />
  ),
  [BillingRuleConditionTarget.ReceiverCity]: (props) => (
    <CityInput {...props} sx={{ minWidth: 200, display: "flex" }} />
  ),
};

type ConditionValueSelectorProps = {
  target: BillingRuleConditionTarget;
  operator: BillingRuleConditionOperator;
  value: number | string | string[] | null;
  onChange: (value: number | string | string[] | null) => void;
  customer?: GetCustomerDetailsQuery["customerById"];
  placeholder?: string;
};

const ConditionValueSelector = ({
  target,
  operator,
  value,
  onChange,
  customer,
  placeholder,
}: ConditionValueSelectorProps) => {
  const { t } = useTranslation("billingRules");
  const ValueSelectorComponent =
    ConditionValueSelectorComponentByTarget[target];

  if (!ValueSelectorComponent) {
    return (
      <TextField
        size="small"
        value={value || ""}
        inputProps={{ "data-testid": testIds.conditionValueSelectorEnterValue } as React.InputHTMLAttributes<HTMLInputElement>}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder || "Enter value"}
        sx={{ minWidth: 200 }}
      />
    );
  }

  // Handle "in between" operator with two value inputs
  if (operator === BillingRuleConditionOperator.IsBetween) {
    const valueArray = Array.isArray(value) ? value : [value, null];
    return (
      <Stack direction="row" alignItems="center" spacing={1}>
        <ValueSelectorComponent
          value={valueArray[0]}
          onChange={(newValue) => {
            onChange([newValue, valueArray[1]] as any);
          }}
          customer={customer}
          placeholder={placeholder || "Min value"}
        />
        <Typography variant="body2">{t("form.and")}</Typography>
        <ValueSelectorComponent
          value={valueArray[1]}
          onChange={(newValue) => {
            onChange([valueArray[0], newValue] as any);
          }}
          customer={customer}
          placeholder={placeholder || "Max value"}
        />
      </Stack>
    );
  }

  // Handle single value for all other operators
  return (
    <ValueSelectorComponent
      value={value}
      onChange={onChange}
      customer={customer}
      placeholder={placeholder}
    />
  );
};

export default ConditionValueSelector;
